import React, { useCallback, useEffect, useState } from "react";
import { FiCheckSquare, FiRefreshCw } from "react-icons/fi";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import LoadingOverlay from "../../components/LoadingOverlay";
import ApprovedSubmissionsTab from "../../components/timesheet/approvals/ApprovedSubmissionsTab";
import { fetchTimesheetApprovalInbox } from "../../services/timesheetApi";
import {
  APPROVAL_TABS,
  canViewApprovedTimesheets,
  resolveApprovalTab,
} from "../../utils/timesheetApprovalAccess";
import { formatHours, normalizeDateKey } from "../../utils/timesheetUtils";

const TAB_OPTIONS = [
  { value: APPROVAL_TABS.PENDING, label: "รออนุมัติ" },
  { value: APPROVAL_TABS.APPROVED, label: "อนุมัติแล้ว" },
];

const formatPeriod = (item) => {
  const start = normalizeDateKey(item?.periodStart);
  const end = normalizeDateKey(item?.periodEnd);
  return start && end ? `${start} ถึง ${end}` : "-";
};

const TimesheetApprovalsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const canViewApproved = canViewApprovedTimesheets();
  const requestedTab = searchParams.get("tab");
  const activeTab = resolveApprovalTab(requestedTab, canViewApproved);
  const isPendingTab = activeTab === APPROVAL_TABS.PENDING;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadInbox = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setItems(await fetchTimesheetApprovalInbox());
    } catch (requestError) {
      const status = requestError?.response?.status;
      const message = status === 403
        ? "คุณไม่มีสิทธิ์อนุมัติ Timesheet"
        : requestError?.response?.data?.message || "ไม่สามารถโหลดรายการอนุมัติ Timesheet ได้";
      setError(message);
      if (status !== 403) toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  // ไม่มีสิทธิ์ดู tab อนุมัติแล้ว ให้ล้าง ?tab ออกแล้วอยู่ที่ tab รออนุมัติ
  useEffect(() => {
    if (!requestedTab || requestedTab === activeTab) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("tab");
    setSearchParams(nextParams, { replace: true, state: location.state });
  }, [activeTab, location.state, requestedTab, searchParams, setSearchParams]);

  useEffect(() => {
    if (isPendingTab) loadInbox();
  }, [isPendingTab, loadInbox]);

  const changeTab = (tab) => {
    const nextParams = new URLSearchParams(searchParams);
    if (tab === APPROVAL_TABS.APPROVED) nextParams.set("tab", tab);
    else nextParams.delete("tab");
    setSearchParams(nextParams);
  };

  useEffect(() => {
    const notification = location.state?.timesheetApprovalNotification;
    if (loading || !notification?.message) return;

    toast.success(notification.message, {
      autoClose: 4000,
      toastId: `timesheet-approval-${notification.action || "success"}`,
    });
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [loading, location.pathname, location.search, location.state, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 px-4 py-5 sm:px-6" aria-busy={isPendingTab && loading}>
      <LoadingOverlay loading={isPendingTab && loading} />
      <div className="rounded-3xl border border-indigo-100 bg-gradient-to-br from-white via-indigo-50/50 to-sky-50/70 p-6 shadow-sm dark:border-indigo-900/40 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 sm:p-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300"><FiCheckSquare size={22} /></div>
            <div><h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 sm:text-3xl">อนุมัติ Timesheet</h1><p className="mt-2 text-sm text-gray-600 dark:text-gray-400">ตรวจสอบ Timesheet รายสัปดาห์ที่ส่งมาและมอบหมายให้คุณอนุมัติ</p></div>
          </div>
          {isPendingTab && <button type="button" onClick={loadInbox} disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"><FiRefreshCw className={loading ? "animate-spin" : ""} /> รีเฟรช</button>}
        </div>
        {canViewApproved && (
          <div className="mt-6 inline-flex rounded-2xl border border-gray-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-800" role="tablist">
            {TAB_OPTIONS.map((tab) => (
              <button key={tab.value} type="button" role="tab" aria-selected={activeTab === tab.value} onClick={() => changeTab(tab.value)} className={`rounded-xl px-4 py-2 text-sm font-medium transition ${activeTab === tab.value ? "bg-indigo-500 text-white shadow-sm" : "text-gray-600 hover:text-indigo-600 dark:text-gray-300 dark:hover:text-indigo-300"}`}>
                {tab.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {!isPendingTab ? <ApprovedSubmissionsTab /> : <div className="mt-6 overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        {loading ? <p className="px-6 py-14 text-center text-sm text-gray-500 dark:text-gray-400">กำลังโหลดรายการอนุมัติ...</p> : error ? <p className="px-6 py-12 text-center text-sm font-medium text-red-600 dark:text-red-300">{error}</p> : !items.length ? <p className="px-6 py-14 text-center text-sm text-gray-500 dark:text-gray-400">ไม่มี Timesheet ที่รอการอนุมัติ</p> : (
          <div className="overflow-x-auto"><table className="min-w-[900px] w-full text-sm"><thead className="bg-gray-50 dark:bg-gray-900/60"><tr><th className="px-4 py-3 text-left">พนักงาน</th><th className="px-4 py-3 text-left">แผนก</th><th className="px-4 py-3 text-left">ช่วงเวลา</th><th className="px-4 py-3 text-right">ชั่วโมง</th><th className="px-4 py-3 text-left">วันที่ส่ง</th><th className="px-4 py-3 text-center">ลำดับการอนุมัติ</th><th className="px-4 py-3" /></tr></thead><tbody className="[&>tr:nth-child(even)]:bg-slate-50/80 dark:[&>tr:nth-child(even)]:bg-gray-900/35">{items.map((item) => { const id = item._id || item.id || item.submissionId; const user = item.user || item.employee || item.submitter || {}; return <tr key={id} className="border-t border-gray-100 dark:border-gray-700/80"><td className="max-w-[220px] break-all px-4 py-4 font-medium text-gray-900 dark:text-gray-100">{user.username || user.email || item.username || item.email || "-"}</td><td className="px-4 py-4 text-gray-600 dark:text-gray-300">{user.department || item.department || "-"}</td><td className="px-4 py-4 text-gray-600 dark:text-gray-300">{formatPeriod(item)}</td><td className="px-4 py-4 text-right text-gray-700 dark:text-gray-200">{formatHours(item.totalHours)}</td><td className="px-4 py-4 text-gray-600 dark:text-gray-300">{item.submittedAt ? new Date(item.submittedAt).toLocaleString() : "-"}</td><td className="px-4 py-4 text-center text-gray-700 dark:text-gray-200">{item.currentApprovalLevel || "-"}</td><td className="px-4 py-4 text-right"><button type="button" onClick={() => navigate(`/timesheets/approvals/${id}`)} className="rounded-lg bg-indigo-500 px-3 py-2 text-xs font-medium text-white hover:bg-indigo-600">ตรวจสอบ</button></td></tr>; })}</tbody></table></div>
        )}
      </div>}
    </div>
  );
};

export default TimesheetApprovalsPage;
